import { useEffect, useState } from "react";
import { LogOut, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fetchConfig, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { TeamCard } from "@/components/coach/TeamCard";
import { TeamFormDialog } from "@/components/coach/TeamFormDialog";
import { AccessBanner } from "@/components/coach/AccessBanner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CoachDashboard() {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [teams, setTeams] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dupOf, setDupOf] = useState(null);
  const [delOf, setDelOf] = useState(null);

  const load = () => api.teams().then(setTeams);
  useEffect(() => { fetchConfig().then(setConfig); load(); }, []);

  const guard = (fn, msg) => async (...args) => {
    try { await fn(...args); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const create = guard((f) => api.createTeam({ ...f, season: f.season || null }), "Team created");
  const duplicate = guard((f) => api.duplicateTeam(dupOf.id, { name: f.name, level: f.level, season: f.season || null }), "Playbook duplicated");
  const remove = guard(() => api.deleteTeam(delOf.id), "Team deleted");

  if (!config || !teams) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading...</div>;

  return (
    <PageShell>
      <AppHeader>
        <button className="nav-btn" data-testid="logout-button" onClick={logout}><LogOut size={18} /> Log out</button>
      </AppHeader>
      <div className="flex flex-wrap items-end justify-between gap-3 pt-2">
        <div>
          <p className="ctl-label">Coach dashboard</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight" data-testid="coach-greeting">Hi, {user.name}</h1>
        </div>
        <button className="btn-primary" data-testid="create-team-button" disabled={!user.access.has_access} onClick={() => setCreating(true)}><Plus size={20} /> New team</button>
      </div>
      <AccessBanner access={user.access} />
      {teams.length === 0 ? (
        <div className="card text-center space-y-3 !py-10" data-testid="no-teams-state">
          <p className="font-heading text-2xl font-bold uppercase">No teams yet</p>
          <p className="text-[var(--muted)] font-medium">Create a team to start from the default playbook and make it your own.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="team-list">
          {teams.map((t) => (
            <TeamCard key={t.id} team={t} levelName={config.sports[t.sport][t.level].name} onDuplicate={() => setDupOf(t)} onDelete={() => setDelOf(t)} />
          ))}
        </div>
      )}
      {creating && (
        <TeamFormDialog open onOpenChange={setCreating} title="New team" config={config} testPrefix="create-team"
          initial={{ name: "", season: "", sport: "baseball", level: "ll" }} submitLabel="Create team" onSubmit={create} />
      )}
      {dupOf && (
        <TeamFormDialog open onOpenChange={(o) => !o && setDupOf(null)} title={`Duplicate ${dupOf.name}`} config={config} lockSport testPrefix="duplicate-team"
          initial={{ name: `${dupOf.name} (copy)`, season: "", sport: dupOf.sport, level: dupOf.level }} submitLabel="Duplicate playbook" onSubmit={duplicate} />
      )}
      <AlertDialog open={!!delOf} onOpenChange={(o) => !o && setDelOf(null)}>
        <AlertDialogContent data-testid="delete-team-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delOf?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the team, its custom plays and its join link. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-team-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-team-confirm" onClick={() => remove().catch(() => {})}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
